import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  AutoAwesome as AIIcon,
  Visibility as PreviewIcon,
  Edit as EditIcon,
  Upload as UploadIcon,
  AccountTree as DataMapIcon,
  Map as RoadmapIcon,
} from '@mui/icons-material';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const DOCUMENT_TYPES = ['PRD', 'Roadmap', 'Data Map', 'Architecture', 'Process', 'Other'];
const DOCUMENT_STATUSES = ['Draft', 'In Review', 'Approved', 'Archived'];
const DOC_STATUS_COLORS = { Draft: '#9e9e9e', 'In Review': '#ffa726', Approved: '#66bb6a', Archived: '#b0bec5' };

// ── Auto-suggest ───────────────────────────────────────────────────────────────

function tokenize(str) {
  return new Set((str || '').toLowerCase().split(/\W+/).filter(Boolean));
}

function formatBudget(budget) {
  if (!budget) return null;
  const total = (budget.capex || 0) + (budget.opex || 0);
  return total > 0 ? `$${(total / 1000).toFixed(0)}k` : null;
}

function suggestLinks(doc, context) {
  const words = tokenize(
    (doc.name || '') + ' ' + (doc.description || '') + ' ' + (doc.body || '')
  );
  const linked = new Set([
    ...(doc.links?.outcomes || []),
    ...(doc.links?.applications || []),
    ...(doc.links?.functions || []),
  ]);

  const score = (name) =>
    [...tokenize(name)].filter((w) => w.length > 3 && words.has(w)).length;

  return {
    outcomes: (context.outcomes || [])
      .filter((o) => !linked.has(o.id) && score(o.name) > 0)
      .sort((a, b) => score(b.name) - score(a.name))
      .slice(0, 5),
    applications: (context.applications || [])
      .filter((a) => !linked.has(a.id) && score(a.name) > 0)
      .sort((a, b) => score(b.name) - score(a.name))
      .slice(0, 5),
    functions: (context.functions || [])
      .filter((f) => !linked.has(f) && score(f) > 0)
      .sort((a, b) => score(b) - score(a))
      .slice(0, 5),
  };
}

// ── Simple markdown renderer ───────────────────────────────────────────────────

function MarkdownPreview({ body }) {
  // Minimal rendering: headings, bold, italic, code, lists
  const html = useMemo(() => {
    if (!body) return '<p style="color:#9e9e9e">Nothing to preview.</p>';
    let text = body
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    // Block elements
    text = text.replace(/^#{3} (.+)$/gm, '<h3>$1</h3>');
    text = text.replace(/^#{2} (.+)$/gm, '<h2>$1</h2>');
    text = text.replace(/^# (.+)$/gm, '<h1>$1</h1>');
    text = text.replace(/^- (.+)$/gm, '<li>$1</li>');
    text = text.replace(/(<li>.*<\/li>\n?)+/g, (m) => `<ul>${m}</ul>`);
    // Inline
    text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    text = text.replace(/\*(.+?)\*/g, '<em>$1</em>');
    text = text.replace(/`(.+?)`/g, '<code style="background:#f5f5f5;padding:2px 4px;border-radius:3px">$1</code>');
    // Paragraphs
    text = text
      .split(/\n\n+/)
      .map((p) => {
        if (/^<(h[123]|ul|li)/.test(p.trim())) return p;
        return `<p>${p.replace(/\n/g, '<br/>')}</p>`;
      })
      .join('\n');

    return text;
  }, [body]);

  return (
    <Box
      sx={{ p: 2, minHeight: 300, fontFamily: 'inherit', lineHeight: 1.7, fontSize: 14 }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

// ── Link section ───────────────────────────────────────────────────────────────

function LinkSection({ title, confirmed, suggestions, allItems, onAdd, onRemove, isReadOnly, renderOption }) {
  const [inputValue, setInputValue] = useState('');

  const confirmedIds = new Set(confirmed.map((c) => c.id ?? c));
  const options = (allItems || []).filter((item) => !confirmedIds.has(item.id ?? item));

  return (
    <Accordion disableGutters defaultExpanded sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 36 }}>
        <Typography variant="body2" fontWeight={600}>{title}</Typography>
        {confirmed.length > 0 && (
          <Chip label={confirmed.length} size="small" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, pt: 0 }}>
        {confirmed.length === 0 && suggestions.length === 0 && isReadOnly && (
          <Typography variant="caption" color="text.secondary">No links yet.</Typography>
        )}

        {/* Confirmed */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: suggestions.length > 0 ? 1 : 0 }}>
          {confirmed.map((item) => (
            <Chip
              key={item.id ?? item}
              label={item.name ?? item}
              size="small"
              color="primary"
              variant="outlined"
              onDelete={isReadOnly ? undefined : () => onRemove(item.id ?? item)}
            />
          ))}
        </Box>

        {/* Suggested */}
        {suggestions.length > 0 && !isReadOnly && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Suggested
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {suggestions.map((item) => (
                <Chip
                  key={item.id ?? item}
                  label={item.name ?? item}
                  size="small"
                  variant="outlined"
                  sx={{ borderColor: '#ffa726', color: '#e65100', fontSize: '0.7rem' }}
                  icon={<AddIcon sx={{ fontSize: '14px !important' }} />}
                  onClick={() => onAdd(item)}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Manual search */}
        {!isReadOnly && (
          <Autocomplete
            size="small"
            options={options}
            getOptionLabel={(opt) => opt.name ?? opt}
            inputValue={inputValue}
            onInputChange={(_, v) => setInputValue(v)}
            onChange={(_, item) => {
              if (item) { onAdd(item); setInputValue(''); }
            }}
            renderOption={renderOption}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={`Search ${title.toLowerCase()}…`}
                size="small"
                variant="outlined"
                sx={{ mt: 1 }}
              />
            )}
            sx={{ mt: 1 }}
            noOptionsText={`No ${title.toLowerCase()} found`}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

function DocEditor({ doc: initialDoc, context, roster = [], isReadOnly, onSave, onCancel, onDelete, onOpenCanvas, onOpenRoadmap }) {
  const isNew = !initialDoc?.id;

  const [doc, setDoc] = useState(() => ({
    id: initialDoc?.id || null,
    name: initialDoc?.name || '',
    type: initialDoc?.type || '',
    description: initialDoc?.description || '',
    body: initialDoc?.body || '',
    tags: initialDoc?.tags || [],
    links: initialDoc?.links || { outcomes: [], applications: [], functions: [] },
    transcript: initialDoc?.transcript || '',
    filename: initialDoc?.filename || null,
    uploaded_at: initialDoc?.uploaded_at || null,
    size_bytes: initialDoc?.size_bytes || 0,
    owner: initialDoc?.owner || null,
    data_map_id: initialDoc?.data_map_id || null,
    roadmap_id: initialDoc?.roadmap_id || null,
    status: initialDoc?.status || 'Draft',
  }));

  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [enhancing, setEnhancing] = useState(false);
  const [error, setError] = useState('');
  const [tagInput, setTagInput] = useState('');
  const transcriptFileRef = useRef(null);

  const suggestions = useMemo(
    () => suggestLinks(doc, context),
    // Re-run when name/description/body/links change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [doc.name, doc.description, doc.body, doc.links, context]
  );

  // Look up confirmed link objects from context for display
  const confirmedOutcomes = useMemo(
    () => (doc.links?.outcomes || []).map((id) => context.outcomes?.find((o) => o.id === id) || { id, name: id }),
    [doc.links?.outcomes, context.outcomes]
  );
  const confirmedApps = useMemo(
    () => (doc.links?.applications || []).map((id) => context.applications?.find((a) => a.id === id) || { id, name: id }),
    [doc.links?.applications, context.applications]
  );
  const confirmedFunctions = useMemo(
    () => (doc.links?.functions || []).map((f) => ({ id: f, name: f })),
    [doc.links?.functions]
  );

  const updateField = useCallback((field, value) => {
    setDoc((prev) => ({ ...prev, [field]: value }));
  }, []);

  const addLink = useCallback((category, item) => {
    setDoc((prev) => ({
      ...prev,
      links: {
        ...prev.links,
        [category]: [...(prev.links[category] || []), item.id ?? item],
      },
    }));
  }, []);

  const removeLink = useCallback((category, idOrValue) => {
    setDoc((prev) => ({
      ...prev,
      links: {
        ...prev.links,
        [category]: (prev.links[category] || []).filter((v) => v !== idOrValue),
      },
    }));
  }, []);

  const addTag = useCallback(() => {
    const t = tagInput.trim();
    if (t && !doc.tags.includes(t)) {
      setDoc((prev) => ({ ...prev, tags: [...prev.tags, t] }));
    }
    setTagInput('');
  }, [tagInput, doc.tags]);

  const handleSave = async () => {
    if (!doc.name.trim()) { setError('Document name is required.'); return; }
    if (!doc.type) { setError('Document type is required.'); return; }

    setSaving(true);
    setError('');
    try {
      let saved;
      if (isNew) {
        let canvasId = null;
        let roadmapId = null;
        if (doc.type === 'Data Map') {
          const dmRes = await fetch(`${API_BASE}/api/data-maps/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: doc.name }),
          });
          if (!dmRes.ok) throw new Error(`Failed to create data map canvas (${dmRes.status})`);
          const canvas = await dmRes.json();
          canvasId = canvas.id;
        }
        if (doc.type === 'Roadmap') {
          const rmRes = await fetch(`${API_BASE}/api/roadmaps/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: doc.name, description: doc.description }),
          });
          if (!rmRes.ok) throw new Error(`Failed to create roadmap (${rmRes.status})`);
          const rm = await rmRes.json();
          roadmapId = rm.id;
        }
        const res = await fetch(`${API_BASE}/api/doc-hub/documents`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: doc.name,
            type: doc.type,
            description: doc.description,
            body: doc.body,
            tags: doc.tags,
            links: doc.links,
            owner: doc.owner,
            data_map_id: canvasId,
            roadmap_id: roadmapId,
            status: doc.status,
          }),
        });
        if (!res.ok) throw new Error(`Create failed (${res.status})`);
        saved = await res.json();
      } else {
        const res = await fetch(`${API_BASE}/api/doc-hub/${doc.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: doc.name,
            type: doc.type,
            description: doc.description,
            body: doc.body,
            tags: doc.tags,
            links: doc.links,
            transcript: doc.transcript,
            owner: doc.owner,
            status: doc.status,
          }),
        });
        if (!res.ok) throw new Error(`Save failed (${res.status})`);
        saved = await res.json();
      }
      setDoc((prev) => ({ ...prev, ...saved }));
      onSave(saved);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!doc.id) { onDelete(null); return; }
    if (!window.confirm(`Delete "${doc.name}"?`)) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_BASE}/api/doc-hub/${doc.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      onDelete(doc.id);
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  const handleEnhance = async () => {
    if (!doc.id) { setError('Save the document first before enhancing.'); return; }
    setEnhancing(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/doc-hub/${doc.id}/enhance`, { method: 'POST' });
      if (!res.ok) throw new Error(`Enhance failed (${res.status})`);
      const data = await res.json();
      updateField('body', data.body);
    } catch (err) {
      setError(err.message);
    } finally {
      setEnhancing(false);
    }
  };

  const handleTranscriptFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => updateField('transcript', ev.target.result);
    reader.readAsText(file);
    e.target.value = '';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Top bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <IconButton size="small" onClick={onCancel}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {isNew ? 'New Document' : 'Edit Document'}
        </Typography>

        {doc.type === 'Data Map' && doc.data_map_id && onOpenCanvas && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<DataMapIcon />}
            onClick={() => onOpenCanvas(doc.data_map_id)}
            sx={{ borderColor: '#80cbc4', color: '#00897b' }}
          >
            Open Canvas
          </Button>
        )}

        {doc.type === 'Roadmap' && doc.roadmap_id && onOpenRoadmap && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<RoadmapIcon />}
            onClick={() => onOpenRoadmap(doc.roadmap_id)}
            sx={{ borderColor: '#80cbc4', color: '#00897b' }}
          >
            Open Roadmap
          </Button>
        )}

        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ py: 0, flexBasis: '100%' }}>
            {error}
          </Alert>
        )}

        {!isReadOnly && (
          <>
            {doc.id && doc.filename && (
              <Tooltip title="Download file">
                <IconButton
                  size="small"
                  onClick={() => { window.location.href = `${API_BASE}/api/doc-hub/${doc.id}/download`; }}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Button
              size="small"
              color="error"
              startIcon={deleting ? <CircularProgress size={14} /> : <DeleteIcon />}
              onClick={handleDelete}
              disabled={deleting || saving}
            >
              Delete
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving || deleting}
              sx={{ bgcolor: '#00897b', '&:hover': { bgcolor: '#00695c' } }}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </>
        )}
        {isReadOnly && doc.id && doc.filename && (
          <Tooltip title="Download file">
            <IconButton
              size="small"
              onClick={() => { window.location.href = `${API_BASE}/api/doc-hub/${doc.id}/download`; }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Body */}
      <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* Left panel — editor */}
        <Box
          sx={{
            flex: '0 0 60%',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            overflow: 'auto',
            pr: 1,
          }}
        >
          <TextField
            label="Document Name"
            value={doc.name}
            onChange={(e) => updateField('name', e.target.value)}
            fullWidth
            required
            InputProps={{ readOnly: isReadOnly }}
            size="small"
          />

          {/* Editor / Preview toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
              Body (Markdown)
            </Typography>
            {!isReadOnly && (
              <Button
                size="small"
                startIcon={preview ? <EditIcon /> : <PreviewIcon />}
                onClick={() => setPreview((p) => !p)}
                sx={{ fontSize: '0.7rem', py: 0 }}
              >
                {preview ? 'Edit' : 'Preview'}
              </Button>
            )}
          </Box>

          {preview || isReadOnly ? (
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                minHeight: 300,
                overflow: 'auto',
              }}
            >
              <MarkdownPreview body={doc.body} />
            </Box>
          ) : (
            <TextField
              value={doc.body}
              onChange={(e) => updateField('body', e.target.value)}
              multiline
              minRows={14}
              fullWidth
              placeholder="Write your document content in Markdown…"
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
            />
          )}
        </Box>

        {/* Right panel — metadata + links + transcript */}
        <Box
          sx={{
            flex: '0 0 40%',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            overflow: 'auto',
            pl: 1,
            borderLeft: '1px solid',
            borderColor: 'divider',
          }}
        >
          {/* Metadata */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Metadata</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <FormControl size="small" fullWidth required>
                <InputLabel>Type</InputLabel>
                <Select
                  value={doc.type}
                  onChange={(e) => updateField('type', e.target.value)}
                  label="Type"
                  disabled={isReadOnly}
                >
                  {DOCUMENT_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={doc.status || 'Draft'}
                  onChange={(e) => updateField('status', e.target.value)}
                  label="Status"
                  disabled={isReadOnly}
                  renderValue={(val) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: DOC_STATUS_COLORS[val] || '#9e9e9e', flexShrink: 0 }} />
                      {val}
                    </Box>
                  )}
                >
                  {DOCUMENT_STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: DOC_STATUS_COLORS[s] || '#9e9e9e' }} />
                        {s}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Description"
                value={doc.description}
                onChange={(e) => updateField('description', e.target.value)}
                fullWidth
                multiline
                rows={2}
                size="small"
                InputProps={{ readOnly: isReadOnly }}
              />

              <Autocomplete
                size="small"
                options={roster}
                getOptionLabel={(m) => m ? `${m.name} (${m.role})` : ''}
                value={roster.find((m) => m.id === doc.owner) || null}
                onChange={(_, m) => updateField('owner', m ? m.id : null)}
                disabled={isReadOnly}
                clearOnEscape
                isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                renderInput={(params) => <TextField {...params} label="Owner" size="small" />}
              />

              {/* Tags */}
              <Box>
                <Typography variant="caption" color="text.secondary">Tags</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {doc.tags.map((t) => (
                    <Chip
                      key={t}
                      label={t}
                      size="small"
                      onDelete={isReadOnly ? undefined : () => updateField('tags', doc.tags.filter((x) => x !== t))}
                    />
                  ))}
                </Box>
                {!isReadOnly && (
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                    <TextField
                      size="small"
                      placeholder="Add tag…"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                      sx={{ flex: 1 }}
                      inputProps={{ style: { fontSize: 12 } }}
                    />
                    <Button size="small" onClick={addTag} sx={{ minWidth: 0, px: 1 }}>+</Button>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          <Divider />

          {/* Links */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Links</Typography>

            <LinkSection
              title="Outcomes"
              confirmed={confirmedOutcomes}
              suggestions={suggestions.outcomes}
              allItems={context.outcomes || []}
              onAdd={(item) => addLink('outcomes', item)}
              onRemove={(id) => removeLink('outcomes', id)}
              isReadOnly={isReadOnly}
              renderOption={(props, opt) => {
                const budget = formatBudget(opt.approved_budget);
                return (
                  <li {...props} key={opt.id ?? opt}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.1 }}>
                      <Typography variant="body2">{opt.name ?? opt}</Typography>
                      {(opt.status || budget) && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                          {[opt.status, budget].filter(Boolean).join(' · ')}
                        </Typography>
                      )}
                    </Box>
                  </li>
                );
              }}
            />

            <LinkSection
              title="Applications"
              confirmed={confirmedApps}
              suggestions={suggestions.applications}
              allItems={context.applications || []}
              onAdd={(item) => addLink('applications', item)}
              onRemove={(id) => removeLink('applications', id)}
              isReadOnly={isReadOnly}
            />

            <LinkSection
              title="Functions"
              confirmed={confirmedFunctions}
              suggestions={suggestions.functions.map((f) => ({ id: f, name: f }))}
              allItems={(context.functions || []).map((f) => ({ id: f, name: f }))}
              onAdd={(item) => addLink('functions', item)}
              onRemove={(id) => removeLink('functions', id)}
              isReadOnly={isReadOnly}
            />
          </Box>

          <Divider />

          {/* Transcript */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Transcript</Typography>
            <TextField
              value={doc.transcript}
              onChange={(e) => updateField('transcript', e.target.value)}
              multiline
              minRows={4}
              fullWidth
              placeholder="Paste a meeting transcript here…"
              size="small"
              InputProps={{ readOnly: isReadOnly }}
            />
            {!isReadOnly && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  component="label"
                  sx={{ fontSize: '0.7rem' }}
                >
                  Upload .txt
                  <input type="file" accept=".txt,text/plain" hidden ref={transcriptFileRef} onChange={handleTranscriptFile} />
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={enhancing ? <CircularProgress size={14} /> : <AIIcon />}
                  onClick={handleEnhance}
                  disabled={enhancing || !doc.id}
                  sx={{ fontSize: '0.7rem', borderColor: '#7c4dff', color: '#7c4dff', '&:hover': { borderColor: '#512da8' } }}
                >
                  {enhancing ? 'Enhancing…' : 'Enhance with AI'}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default DocEditor;
